import { useEffect, useRef, useState, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import _ from 'lodash/collection';
import Joi from 'joi';
import Calendar from 'react-calendar';

import i18n from '../../i18n';

import { useGlobalContext } from '../../context/GlobalContext';
import serviceService from '../../services/serviceService';
import { scrollTop, sortPastServices } from '../../utils/utilities';
import sendDiscordMessage from '../../utils/discord';
import { sortItem } from '../../utils/sorting';
import { titleForDate } from '../../utils/serviceTitle';
import { persistSongsServicePlay } from '../../helpers/playHistory';
import useForm from '../../hooks/useForm';
import { renderInput } from '../../components/common/renderUtils';

import NoContent from '../../components/common/NoContent';
import ServiceSongs from '../../components/ServiceSongs';
import PageBar from '../../components/PageBar';
import Footer from '../../components/common/Footer';
import ServiceLength from '../../components/common/ServiceLength';
import useSongLengths from '../../hooks/useSongLengths';
import { totalSeconds } from '../../helpers/serviceLength';
import ServiceActions from '../../components/ServiceActions/ServiceActions';
import Icon from '../../components/common/Icon';

import { ReactComponent as XmarkIcon } from '../../icons/xmark.svg';
import { ReactComponent as SaveIcon } from '../../icons/floppy-disk.svg';
import { ReactComponent as InfoIcon } from '../../icons/info-circle-solid.svg';

import './ServiceForm.scss';

const ServiceForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const { songs, setSongs, services, setServices, currentServiceForm, setCurrentServiceForm } = useGlobalContext();

  const [savingService, setSavingService] = useState(false);
  const loadedServiceIdRef = useRef(currentServiceForm.editServiceId);

  const getCurrenServiceSongs = useCallback(
    (songsIds) => {
      let currentServiceSongs = [];

      for (let i = 0; i < songsIds.length; i++) {
        const song = songs.find((s) => s._id === songsIds[i]);
        if (song) {
          currentServiceSongs.push(song);
        }
      }

      return currentServiceSongs;
    },
    [songs],
  );

  // Load service data when editing
  useEffect(() => {
    if (id && id !== 'new' && services.length > 0 && songs.length > 0) {
      const service = services.find((s) => s._id === id);
      if (service) {
        // Only load service if it's not already set (prevents overwriting suggested songs)
        if (loadedServiceIdRef.current !== service._id) {
          loadedServiceIdRef.current = service._id;
          const currentServiceSongs = getCurrenServiceSongs(service.songsIds);
          setCurrentServiceForm({
            songs: currentServiceSongs,
            newSongs: [],
            title: service.title,
            date: new Date(service.date),
            editServiceId: service._id,
          });
        }
      } else {
        toast.error(i18n.t('serviceForm.notFound'));
        history.push('/songs');
      }
    } else if (id === 'new') {
      // Creating new service - check if we have URL params for suggested songs
      const urlParams = new URLSearchParams(window.location.search);
      const suggestedSongsParam = urlParams.get('suggestedSongs');

      if (suggestedSongsParam && songs.length > 0) {
        const songIds = suggestedSongsParam.split(',').filter((songId) => songId.trim());

        if (songIds.length > 0) {
          // Find valid songs that exist in the songs list
          const validSongs = songIds
            .map((songId) => songs.find((song) => song._id === songId.trim()))
            .filter((song) => song !== undefined);

          if (validSongs.length > 0) {
            setCurrentServiceForm((prev) => ({
              ...prev,
              songs: validSongs,
            }));
            // Clear the URL param
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }
      }
    }
  }, [id, services, songs, setCurrentServiceForm, history, getCurrenServiceSongs]);

  const getCurrentServiceIds = () => {
    const currentServiceIds = [];
    for (let i = 0; i < currentServiceForm.songs.length; i++) {
      currentServiceIds.push(currentServiceForm.songs[i]._id);
    }

    return currentServiceIds;
  };

  const addYoutubeLinks = (service) => {
    const links = [];
    const serviceWithLinks = { ...service };

    for (let songId of serviceWithLinks.songsIds) {
      const song = songs.find((song) => song._id === songId);

      if (song && song.youtube) {
        links.push(song.youtube);
      }
    }

    if (links.length > 0) {
      serviceWithLinks.links = links;
      return serviceWithLinks;
    }

    return service;
  };

  const handleCreateService = async (title, date) => {
    let servicesData = [...services];
    let updatedSongs = [...songs];

    // A new service
    if (!currentServiceForm.editServiceId) {
      try {
        setSavingService(true);
        // Save Service
        const { data: newService } = await serviceService.saveService({
          title,
          date,
          songsIds: getCurrentServiceIds(),
        });

        const updatedSongsWithPlayHistory = await persistSongsServicePlay(
          currentServiceForm.songs,
          title,
          updatedSongs,
        );
        updatedSongs = updatedSongsWithPlayHistory;

        // Add Youtube links
        const newServiceWithLinks = addYoutubeLinks(newService);
        servicesData.push(newServiceWithLinks);

        // Sort services
        servicesData = _.orderBy(servicesData, 'date');

        // Push past due services to the bottom
        servicesData = sortPastServices(servicesData);
      } catch (ex) {
        setSavingService(false);
        return;
      }
    } else {
      // Updating a service
      const service = servicesData.find((s) => s._id === currentServiceForm.editServiceId);
      const serviceIndex = servicesData.indexOf(service);

      const updatedService = {
        _id: service._id,
        title: title,
        date: date.toISOString(),
        songsIds: getCurrentServiceIds(),
        teamId: service.teamId,
      };

      // Add YouTube links
      const updatedServiceWithLinks = addYoutubeLinks(updatedService);
      servicesData.splice(serviceIndex, 1, updatedServiceWithLinks);

      // Sort services
      servicesData = _.orderBy(servicesData, 'date');

      // Push past due services to the bottom
      servicesData = sortPastServices(servicesData);

      // Save Service
      try {
        setSavingService(true);

        await serviceService.saveService(updatedService);

        const songsToUpdate = currentServiceForm.newSongs?.length > 0 ? currentServiceForm.newSongs : [];
        if (songsToUpdate.length > 0) {
          const updatedSongsWithPlayHistory = await persistSongsServicePlay(songsToUpdate, title, updatedSongs);
          updatedSongs = updatedSongsWithPlayHistory;
        }

        // Send Discord Message
        sendDiscordMessage(
          {
            currentService: currentServiceForm.songs,
            currentServiceTitle: currentServiceForm.title,
            editServiceId: currentServiceForm.editServiceId,
          },
          service,
          updatedService,
        );
      } catch (ex) {
        setSavingService(false);
        return;
      }
    }

    setSavingService(false);
    setSongs(updatedSongs);
    setServices(servicesData);

    // Reset the service form state
    setCurrentServiceForm({
      songs: [],
      newSongs: [],
      title: '',
      date: new Date(),
      editServiceId: null,
    });

    scrollTop(null, false);
    history.push('/songs');
  };

  const handleDeleteFromService = (song, clearService = false) => {
    let currentServiceSongs = [...currentServiceForm.songs];

    if (!song && clearService) {
      currentServiceSongs = [];
      setCurrentServiceForm((prev) => ({
        ...prev,
        songs: currentServiceSongs,
        newSongs: [],
      }));
    } else {
      currentServiceSongs = currentServiceSongs.filter((s) => s._id !== song._id);
      setCurrentServiceForm((prev) => ({
        ...prev,
        songs: currentServiceSongs,
        newSongs: (prev.newSongs || []).filter((s) => s._id !== song._id),
      }));
    }
  };

  const handleSortInService = (song, direction) => {
    const sorted = sortItem(song, currentServiceForm.songs, direction);
    setCurrentServiceForm((prev) => ({
      ...prev,
      songs: sorted,
    }));
  };

  const handleSortByTypeInService = () => {
    let updatedService = [];

    if (currentServiceForm.songs.length < 1) return;

    const firstSongCat = currentServiceForm.songs[0]?.category.name;
    const worshipSongs = currentServiceForm.songs.filter((s) => s.category.name === 'Worship');
    const praiseSongs = currentServiceForm.songs.filter((s) => s.category.name === 'Praise');

    if (firstSongCat === 'Worship') {
      updatedService = [...praiseSongs, ...worshipSongs];
    } else {
      updatedService = [...worshipSongs, ...praiseSongs];
    }

    setCurrentServiceForm((prev) => ({
      ...prev,
      songs: updatedService,
    }));
  };

  const handleSongServiceSort = useCallback(
    (dragIndex, hoverIndex) => {
      setCurrentServiceForm((prev) => {
        const draggedSong = prev.songs[dragIndex];
        const updatedService = [...prev.songs];

        // Remove the dragged song from its current position
        updatedService.splice(dragIndex, 1);
        // Insert it at the new position
        updatedService.splice(hoverIndex, 0, draggedSong);

        return {
          ...prev,
          songs: updatedService,
        };
      });
    },
    [setCurrentServiceForm],
  );

  const handleToggleCreateService = () => {
    // Navigate back to songs WITHOUT resetting state - state persists in GlobalContext
    history.push('/songs');
    scrollTop();
  };

  const handleClose = () => {
    // Reset form state and navigate back to songs
    setCurrentServiceForm({
      songs: [],
      newSongs: [],
      title: '',
      date: new Date(),
      editServiceId: null,
    });
    history.push('/songs');
  };

  // Form validation schema
  const schema = {
    title: Joi.string().max(25).required().label('Service Title'),
    date: Joi.date().required().label('Date'),
    songCount: Joi.number().min(1).required(),
  };

  const initialData = {
    title: currentServiceForm.title || '',
    date: currentServiceForm.date || new Date(),
    songCount: currentServiceForm.songs.length || 0,
  };

  const { data, setData, errors, handleChange, handleSubmit, isFormValid } = useForm(initialData, schema);

  // Sync form data with currentServiceForm whenever it changes
  useEffect(() => {
    const newData = {
      title: currentServiceForm.title || '',
      date: currentServiceForm.date ? new Date(currentServiceForm.date) : new Date(),
      songCount: currentServiceForm.songs.length,
    };
    setData(newData);
  }, [currentServiceForm.title, currentServiceForm.date, currentServiceForm.songs.length, setData]);

  const handleDateChange = (date) => {
    const title = titleForDate(data.title, date);
    const newData = { ...data };
    newData.date = date;
    newData.title = title;
    setData(newData);
    // Also update GlobalContext so it persists
    setCurrentServiceForm((prev) => ({
      ...prev,
      date: date,
      title,
    }));
  };

  const handleFormChange = (e) => {
    // Update local form state
    handleChange(e);

    // Also update GlobalContext for title so it persists
    if (e.target.name === 'title') {
      setCurrentServiceForm((prev) => ({
        ...prev,
        title: e.target.value,
      }));
    }
  };

  const handleDeleteSong = (song) => {
    // remove 1 from songCount
    const newData = { ...data };
    newData.songCount--;
    setData(newData);
    // pass control
    handleDeleteFromService(song);
  };

  const renderSongCount = (songCount) => {
    if (songCount === 0) return null;
    return <span className="service-form__song-count">{songCount}</span>;
  };

  const renderCurrentSongs = () => {
    return currentServiceForm.songs.length !== 0 ? (
      <ServiceSongs
        currentService={currentServiceForm.songs}
        songLengths={songLengths.lengths}
        onDeleteFromService={handleDeleteSong}
        onSortInService={handleSortInService}
        onDragSortInService={handleSongServiceSort}
      />
    ) : (
      <NoContent message={t('serviceForm.noSongs')} />
    );
  };

  const doSubmit = () => {
    const { title, date } = data;
    handleCreateService(title, date);
  };

  const { songCount } = data;
  const songLengths = useSongLengths(currentServiceForm.songs);

  const getPageTitle = () => {
    if (currentServiceForm.editServiceId) {
      return currentServiceForm.title || t('serviceForm.editService');
    }
    return currentServiceForm.title || t('songsMenu.newService');
  };

  return (
    <>
      {/* Going back keeps the picked songs for the list's pill; ✕ discards them */}
      <PageBar
        back={{ label: t('common.back'), icon: true, onClick: handleToggleCreateService }}
        title={getPageTitle()}
        actions={[
          { label: t('serviceForm.discard'), icon: <XmarkIcon />, onClick: handleClose },
          {
            label: currentServiceForm.editServiceId ? t('serviceForm.save') : t('songsMenu.createService'),
            icon: <SaveIcon />,
            form: 'service-form',
            disabled: !isFormValid(),
            loading: savingService,
          },
        ]}
        sticky
      />
      <div className="mg-container">
        <div className="service-form">
          <form id="service-form" onSubmit={(e) => handleSubmit(e, doSubmit)}>
            <div className="mg-form-row">
              <div className="mg-col-lg-8 service-form__songs-col">
                <div className="service-form__songs">
                  <div className="song-title-container">
                    <h2 className="section-header">
                      {t('songs.title')} {renderSongCount(songCount)}
                      <ServiceLength
                        seconds={totalSeconds(currentServiceForm.songs, songLengths.lengths)}
                        fromCache={songLengths.fromCache}
                        className="service-form__length"
                      />
                    </h2>
                    <ServiceActions
                      onAddSongs={handleToggleCreateService}
                      onDeleteFromService={handleDeleteFromService}
                      onSortByType={handleSortByTypeInService}
                      songCount={songCount}
                    />
                  </div>
                  {renderCurrentSongs()}
                </div>
              </div>
              <div className="mg-col-lg-4 service-form__info-col">
                <div className="service-form__right">
                  <h2 className="section-header header-info">{t('serviceForm.info')}</h2>
                  <Calendar onChange={handleDateChange} value={data.date} minDate={new Date()} locale={i18n.language} />
                  {renderInput('title', t('serviceForm.titleLabel'), 'text', data, errors, handleFormChange, {
                    clearable: true,
                  })}
                  {renderInput('date', t('serviceForm.dateLabel'), 'text', data, errors, handleFormChange)}
                  {/* The backend's TTL index on the service date does the removing */}
                  <small className="mg-form-text expiry-note">
                    <Icon icon={<InfoIcon />} className="solid" />
                    {/* A non-breaking space before the last word, so it never wraps alone */}
                    {t('serviceForm.expiry')}
                  </small>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ServiceForm;
